import type { DateValue } from "@internationalized/date";
import { getLocalTimeZone, parseDate, today } from "@internationalized/date";
import type { RangeValue } from "@react-types/shared";
import {
  Announcement01,
  Announcement03,
  Calendar as CalendarIcon,
  Clipboard,
  ShieldZap,
  ShoppingBag01,
  TrendUp01,
  Users01,
} from "@untitledui/icons";
import type { PropsWithChildren } from "react";
import { useCallback, useMemo } from "react";
import { useDateFormatter } from "react-aria";
import { useAssuranceFilters } from "@/api/assurance/assurance-filters";
import { useVideoContentFeatures } from "@/api/content-features";
import { useCreatorFilters } from "@/api/creator-filters";
import { useVideoFilterRanges, type VideoFilterRanges } from "@/api/filter-ranges";
import { useAdvancedFilters } from "@/api/tiktok-shop";
import { DateRangeCalendarPopover } from "@/components/application/date-picker/date-range-calendar-popover";
import { Button } from "@/components/base/buttons/button";
import { Label } from "@/components/base/input/label";
import { HorizontalTabs } from "@/components/base/tabs/horizontal-tabs";
import {
  CampaignFilter,
  CollaborationFilter,
  ContentFeatureFilter,
  OutcomeFilter,
  ProductFilter,
  RiskTypeFilter,
  SafetyStatusFilter,
} from "@/components/filters/extra-filters";
import { PercentileSelector, type PercentileValue } from "@/components/filters/percentile-selector";
import { PillField } from "@/components/filters/pill-field";
import { FollowersRangeFilter, GmvRangeFilter, PerformanceSimpleRange } from "@/components/filters/range-filters";
import {
  emptyRangeReason,
  FilterPanelContainer,
  FilterPanelHeader,
  FilterSection,
  LabelWithTooltip,
  tabUnavailability,
  UnavailableRange,
  unavailableUnless,
  useUnavailableReasons,
  VIDEO_UNAVAILABLE_REASONS,
  zeroMaxReason,
} from "@/components/filters/shared";
import { Accordion } from "@/components/ui/accordion";
import { Form, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { useIsAssurance } from "@/hooks/use-is-assurance";
import { useFilters } from "@/providers/filter-provider";
import { RISK_TIER_LABELS } from "@/providers/filter-registry";
import { cx } from "@/utils/cx";
import { getDetectedLanguageLabel } from "@/utils/language";
import { toTitleCase } from "@/utils/toTitleCase";

type FilterSectionId =
  | "performance"
  | "content-analysis"
  | "safety-risks"
  | "creator-overview"
  | "featured-products"
  | "collaborations"
  | "ad-campaigns";

type VideoFilterPanelProps = {
  open: boolean;
  close: () => void;
  clearAll?: () => void;
  openSection?: FilterSectionId;
  timePeriod: "7d" | "30d" | "all_time";
  withAvailability?: boolean;
} & PropsWithChildren;

const TIME_PERIOD_TABS = [
  { id: "7d", label: "Last Week" },
  { id: "30d", label: "Last Month" },
  { id: "all_time", label: "All Time" },
];

export const VideoFilterPanel = ({
  open,
  close,
  clearAll,
  openSection,
  timePeriod,
  withAvailability = false,
}: VideoFilterPanelProps) => {
  const { reset } = useFilters();
  const { data: videoFilterRanges } = useVideoFilterRanges({ timePeriod, enabled: open });

  const handleClear = () => {
    clearAll?.();
    reset();
  };

  return (
    <FilterPanelContainer open={open}>
      <FilterPanelHeader onClear={handleClear} onClose={close} />
      <div className="flex-1 overflow-y-auto px-6 pb-6">
        <VideoFilters
          openSection={openSection}
          videoFilterRanges={videoFilterRanges}
          availabilityRanges={withAvailability ? videoFilterRanges : undefined}
        />
      </div>
    </FilterPanelContainer>
  );
};

// =============================================================================
// MAIN FILTERS COMPONENT
// =============================================================================

type VideoFiltersProps = {
  openSection?: FilterSectionId;
  videoFilterRanges?: VideoFilterRanges;
  availabilityRanges?: VideoFilterRanges;
};

const PublishDateFilter = () => {
  const { form } = useFilters();
  const formatter = useDateFormatter({ month: "short", day: "numeric", year: "numeric" });

  const startValue = form.watch("publishDateStart") as string | undefined;
  const endValue = form.watch("publishDateEnd") as string | undefined;

  const parsedValue = useMemo(() => {
    if (!startValue || !endValue) return null;
    return { start: parseDate(startValue), end: parseDate(endValue) };
  }, [startValue, endValue]);

  const formattedRange = useMemo(() => {
    if (!parsedValue) return "Select dates";
    const startStr = formatter.format(parsedValue.start.toDate(getLocalTimeZone()));
    const endStr = formatter.format(parsedValue.end.toDate(getLocalTimeZone()));
    return `${startStr} – ${endStr}`;
  }, [parsedValue, formatter]);

  const isActive = !!startValue && !!endValue;

  const handleChange = useCallback(
    (range: RangeValue<DateValue> | null) => {
      if (range) {
        form.setValue("publishDateStart", range.start.toString());
        form.setValue("publishDateEnd", range.end.toString());
      } else {
        form.setValue("publishDateStart", undefined);
        form.setValue("publishDateEnd", undefined);
      }
    },
    [form],
  );

  const handleReset = useCallback(() => {
    form.setValue("publishDateStart", undefined);
    form.setValue("publishDateEnd", undefined);
  }, [form]);

  return (
    <div className="flex flex-col gap-2">
      <Label className="text-sm text-tertiary">Publish Date</Label>
      <DateRangeCalendarPopover
        value={parsedValue}
        onChange={handleChange}
        onReset={handleReset}
        resetText="Clear"
        placement="bottom left"
        maxValue={today(getLocalTimeZone())}
      >
        <Button
          size="sm"
          color={isActive ? "tertiary" : "secondary"}
          iconLeading={<CalendarIcon className={cx("size-5 text-placeholder", isActive && "text-brand-tertiary")} />}
          className={cx(
            "w-full justify-start text-sm font-medium",
            isActive && "bg-brand-primary text-brand-primary ring-1 hover:bg-brand-secondary ring-inset ring-brand",
          )}
        >
          {formattedRange}
        </Button>
      </DateRangeCalendarPopover>
    </div>
  );
};

const FlaggedDateFilter = () => {
  const { form } = useFilters();
  const formatter = useDateFormatter({ month: "short", day: "numeric", year: "numeric" });

  const startValue = form.watch("flaggedDateStart") as string | undefined;
  const endValue = form.watch("flaggedDateEnd") as string | undefined;

  const parsedValue = useMemo(() => {
    if (!startValue || !endValue) return null;
    return { start: parseDate(startValue), end: parseDate(endValue) };
  }, [startValue, endValue]);

  const formattedRange = useMemo(() => {
    if (!parsedValue) return "Select dates";
    const startStr = formatter.format(parsedValue.start.toDate(getLocalTimeZone()));
    const endStr = formatter.format(parsedValue.end.toDate(getLocalTimeZone()));
    return `${startStr} – ${endStr}`;
  }, [parsedValue, formatter]);

  const isActive = !!startValue && !!endValue;

  const handleChange = useCallback(
    (range: RangeValue<DateValue> | null) => {
      if (range) {
        form.setValue("flaggedDateStart", range.start.toString());
        form.setValue("flaggedDateEnd", range.end.toString());
      } else {
        form.setValue("flaggedDateStart", undefined);
        form.setValue("flaggedDateEnd", undefined);
      }
    },
    [form],
  );

  const handleReset = useCallback(() => {
    form.setValue("flaggedDateStart", undefined);
    form.setValue("flaggedDateEnd", undefined);
  }, [form]);

  return (
    <div className="flex flex-col gap-2">
      <Label className="text-sm text-tertiary">Flagged Date</Label>
      <DateRangeCalendarPopover
        value={parsedValue}
        onChange={handleChange}
        onReset={handleReset}
        resetText="Clear"
        placement="bottom left"
        maxValue={today(getLocalTimeZone())}
      >
        <Button
          size="sm"
          color={isActive ? "tertiary" : "secondary"}
          iconLeading={<CalendarIcon className={cx("size-5 text-placeholder", isActive && "text-brand-tertiary")} />}
          className={cx(
            "w-full justify-start text-sm font-medium",
            isActive && "bg-brand-primary text-brand-primary ring-1 hover:bg-brand-secondary ring-inset ring-brand",
          )}
        >
          {formattedRange}
        </Button>
      </DateRangeCalendarPopover>
    </div>
  );
};

const BRAND_INACTIVE_TIP = {
  title: "No merchant accounts connected",
  description: "Add them in Settings → Merchant details → Merchant accounts to use this filter",
} as const;

const VIDEO_OPTION_REASONS = {
  safetyStatus: VIDEO_UNAVAILABLE_REASONS.safetyStatus,
  riskTier: VIDEO_UNAVAILABLE_REASONS.riskTier,
  riskId: VIDEO_UNAVAILABLE_REASONS.riskType,
  maleAudiencePercent: VIDEO_UNAVAILABLE_REASONS.audienceShare,
  femaleAudiencePercent: VIDEO_UNAVAILABLE_REASONS.audienceShare,
  age: VIDEO_UNAVAILABLE_REASONS.age,
  creatorLevel: VIDEO_UNAVAILABLE_REASONS.creatorLevel,
};

const VideoFilters = ({ openSection, videoFilterRanges, availabilityRanges }: VideoFiltersProps) => {
  const { form, isActive } = useFilters();
  const { data: assuranceData } = useAssuranceFilters();
  const { data: features } = useVideoContentFeatures();
  const { data: advancedFilters } = useAdvancedFilters();
  const isAssurance = useIsAssurance();
  const { data: creatorFiltersMeta, isLoading: isCreatorFiltersLoading } = useCreatorFilters();
  const brandDisabled = !isCreatorFiltersLoading && !creatorFiltersMeta?.hasBrandCreators;
  const availability = availabilityRanges?.availability;
  const unavailable = useUnavailableReasons(availability, VIDEO_OPTION_REASONS);

  return (
    <Form {...form}>
      <div className="flex flex-col gap-6">
        {isActive("timePeriod") && (
          <FormField
            control={form.control}
            name="timePeriod"
            render={({ field }) => (
              <HorizontalTabs
                tabs={TIME_PERIOD_TABS}
                activeTab={field.value ?? "all_time"}
                onTabChange={(tabId) => field.onChange(tabId)}
              />
            )}
          />
        )}
        {isActive("publishDateStart") && isActive("publishDateEnd") && <PublishDateFilter />}
        {isActive("flaggedDateStart") && isActive("flaggedDateEnd") && <FlaggedDateFilter />}

        {/* GMV Histogram */}
        {isActive("minGmv") &&
          isActive("maxGmv") &&
          videoFilterRanges?.gmv &&
          (availabilityRanges || videoFilterRanges.gmv.max > 0) && (
            <UnavailableRange
              form={form}
              fields={["minGmv", "maxGmv"]}
              reason={emptyRangeReason(availabilityRanges?.gmv, VIDEO_UNAVAILABLE_REASONS.gmv)}
            >
              <GmvRangeFilter
                form={form}
                rangeData={videoFilterRanges.gmv}
                max={videoFilterRanges.gmv.max || undefined}
                showHistogram
                tooltip="Gross Merchandise Value"
                description="Total revenue generated from this video."
              />
            </UnavailableRange>
          )}

        <Accordion type="single" collapsible className="w-full" defaultValue={openSection}>
          {/* Performance Section */}
          {(isActive("minViews") || isActive("minCtr") || isActive("minCtor") || isActive("minOrders")) && (
            <FilterSection id="performance" icon={<TrendUp01 className="w-5 h-5" />} title="Performance">
              {isActive("minViews") && isActive("maxViews") && (
                <UnavailableRange
                  form={form}
                  fields={["minViews", "maxViews"]}
                  reason={emptyRangeReason(availabilityRanges?.views, VIDEO_UNAVAILABLE_REASONS.views)}
                >
                  <PerformanceSimpleRange
                    form={form}
                    label="Views"
                    tooltip="Total Views"
                    description="Number of times this video has been viewed."
                    minField="minViews"
                    maxField="maxViews"
                    min={videoFilterRanges?.views?.min ?? 0}
                    max={videoFilterRanges?.views?.max || 10000000}
                    format="followers"
                  />
                </UnavailableRange>
              )}
              {isActive("minCtr") && isActive("maxCtr") && (
                <UnavailableRange
                  form={form}
                  fields={["minCtr", "maxCtr"]}
                  reason={emptyRangeReason(availabilityRanges?.ctr, VIDEO_UNAVAILABLE_REASONS.ctr)}
                >
                  <PerformanceSimpleRange
                    form={form}
                    label="CTR"
                    tooltip="Click-Through Rate"
                    description="Percentage of viewers who clicked the product link."
                    minField="minCtr"
                    maxField="maxCtr"
                    min={videoFilterRanges?.ctr?.min ?? 0}
                    max={videoFilterRanges?.ctr?.max || 100}
                    format="percent"
                  />
                </UnavailableRange>
              )}
              {isActive("minCtor") && isActive("maxCtor") && (
                <UnavailableRange
                  form={form}
                  fields={["minCtor", "maxCtor"]}
                  reason={emptyRangeReason(availabilityRanges?.ctor, VIDEO_UNAVAILABLE_REASONS.ctor)}
                >
                  <PerformanceSimpleRange
                    form={form}
                    label="CTOR"
                    tooltip="Click-to-Order Rate"
                    description="Percentage of clicks that resulted in an order."
                    minField="minCtor"
                    maxField="maxCtor"
                    min={videoFilterRanges?.ctor?.min ?? 0}
                    max={videoFilterRanges?.ctor?.max || 100}
                    format="percent"
                  />
                </UnavailableRange>
              )}
              {isActive("minOrders") && isActive("maxOrders") && (
                <UnavailableRange
                  form={form}
                  fields={["minOrders", "maxOrders"]}
                  reason={emptyRangeReason(availabilityRanges?.orders, VIDEO_UNAVAILABLE_REASONS.orders)}
                >
                  <PerformanceSimpleRange
                    form={form}
                    label="Orders"
                    tooltip="Total Orders"
                    description="Number of orders generated from this video."
                    minField="minOrders"
                    maxField="maxOrders"
                    min={videoFilterRanges?.orders?.min ?? 0}
                    max={videoFilterRanges?.orders?.max || 1000}
                    format="number"
                  />
                </UnavailableRange>
              )}
            </FilterSection>
          )}

          {/* Content Analysis Section */}
          {(isActive("videoStyles") ||
            isActive("hooks") ||
            isActive("sellingPoints") ||
            isActive("tactics") ||
            isActive("videoLanguages")) && (
            <FilterSection id="content-analysis" icon={<Clipboard className="w-5 h-5" />} title="Content Analysis">
              {isActive("videoStyles") && (
                <FormField
                  control={form.control}
                  name="videoStyles"
                  render={({ field }) => (
                    <ContentFeatureFilter
                      title="Video Style"
                      field={field}
                      options={features?.videoStyles.map((f) => ({ id: f, label: f })) ?? []}
                    />
                  )}
                />
              )}
              {isActive("hooks") && (
                <FormField
                  control={form.control}
                  name="hooks"
                  render={({ field }) => (
                    <ContentFeatureFilter
                      title="Hook"
                      field={field}
                      options={features?.hooks.map((f) => ({ id: f, label: f })) ?? []}
                    />
                  )}
                />
              )}
              {isActive("sellingPoints") && (
                <FormField
                  control={form.control}
                  name="sellingPoints"
                  render={({ field }) => (
                    <ContentFeatureFilter
                      title="Selling Point"
                      field={field}
                      options={features?.sellingPoints.map((f) => ({ id: f, label: f })) ?? []}
                    />
                  )}
                />
              )}
              {isActive("tactics") && (
                <FormField
                  control={form.control}
                  name="tactics"
                  render={({ field }) => (
                    <ContentFeatureFilter
                      title="Sales Tactics"
                      field={field}
                      options={features?.tactics.map((f) => ({ id: f, label: toTitleCase(f.replace("_", " ")) })) ?? []}
                    />
                  )}
                />
              )}
              {isActive("videoLanguages") && (
                <FormField
                  control={form.control}
                  name="videoLanguages"
                  render={({ field }) => {
                    const baseOptions = (features?.languages ?? []).map((l) => ({
                      id: l,
                      label: getDetectedLanguageLabel(l),
                    }));
                    const options = features?.hasUnknownLanguage
                      ? [...baseOptions, { id: "__unknown", label: "Not detected" }]
                      : baseOptions;
                    return <ContentFeatureFilter title="Video Language" field={field} options={options} />;
                  }}
                />
              )}
            </FilterSection>
          )}

          {/* Safety Risks Section */}
          {isAssurance &&
            (isActive("videoRiskStatus") ||
              isActive("riskTier") ||
              isActive("riskId") ||
              isActive("safetyStatus") ||
              isActive("actionTaken")) && (
              <FilterSection id="safety-risks" icon={<ShieldZap className="w-5 h-5" />} title="Safety Risks">
                {isActive("videoRiskStatus") && (
                  <FormField
                    control={form.control}
                    name="videoRiskStatus"
                    render={({ field }) => (
                      <div className="flex flex-col gap-2">
                        <LabelWithTooltip
                          label="Video Risk Status"
                          tooltip="Video Risk Status"
                          description="Filter videos based on whether they contain identified risks."
                        />
                        <HorizontalTabs
                          tabs={[
                            { id: "__all", label: "All" },
                            {
                              id: "no_risk",
                              label: "No Risks",
                              ...tabUnavailability(
                                unavailableUnless(
                                  availability?.videoRiskStatus,
                                  VIDEO_UNAVAILABLE_REASONS.noRisk,
                                )?.("no_risk"),
                                field.value === "no_risk",
                              ),
                            },
                            {
                              id: "has_risk",
                              label: "Has Risks",
                              ...tabUnavailability(
                                unavailableUnless(
                                  availability?.videoRiskStatus,
                                  VIDEO_UNAVAILABLE_REASONS.hasRisk,
                                )?.("has_risk"),
                                field.value === "has_risk",
                              ),
                            },
                          ]}
                          activeTab={field.value ?? "__all"}
                          onTabChange={(tabId) => field.onChange(tabId === "__all" ? undefined : tabId)}
                        />
                      </div>
                    )}
                  />
                )}
                {isActive("safetyStatus") && (
                  <FormField
                    control={form.control}
                    name="safetyStatus"
                    render={({ field }) => (
                      <SafetyStatusFilter field={field} getUnavailableReason={unavailable.safetyStatus} />
                    )}
                  />
                )}
                {isActive("riskTier") && (
                  <FormField
                    control={form.control}
                    name="riskTier"
                    render={({ field }) => (
                      <FormItem className="flex flex-col gap-2">
                        <FormLabel className="text-sm text-tertiary">Risk Severity</FormLabel>
                        <PillField
                          value={field.value}
                          options={["1", "2", "3", "4"]}
                          onChange={field.onChange}
                          getLabel={(val) => RISK_TIER_LABELS[val] ?? val}
                          getUnavailableReason={unavailable.riskTier}
                        />
                      </FormItem>
                    )}
                  />
                )}
                {isActive("riskId") && (
                  <FormField
                    control={form.control}
                    name="riskId"
                    render={({ field }) => <RiskTypeFilter field={field} getUnavailableReason={unavailable.riskId} />}
                  />
                )}
                {isActive("actionTaken") && (
                  <FormField
                    control={form.control}
                    name="actionTaken"
                    render={({ field }) => (
                      <OutcomeFilter field={field} options={assuranceData?.resolvedOutcomes ?? []} />
                    )}
                  />
                )}
              </FilterSection>
            )}

          {/* Creator Overview Section */}
          {(isActive("creatorType") ||
            isActive("creatorLevel") ||
            isActive("minFollowers") ||
            isActive("maleAudiencePercent") ||
            isActive("femaleAudiencePercent") ||
            isActive("age")) && (
            <FilterSection id="creator-overview" icon={<Users01 className="w-5 h-5" />} title="Creator Overview">
              {isActive("creatorType") && (
                <FormField
                  control={form.control}
                  name="creatorType"
                  render={({ field }) => (
                    <div className="flex flex-col gap-2">
                      <LabelWithTooltip
                        label="Creator Type"
                        tooltip="Creator Type"
                        description="All – every creator that has posted about your products. Creators – independent creators promoting your products. Brand – TikTok accounts owned by your brand."
                      />
                      <HorizontalTabs
                        tabs={[
                          { id: "all", label: "All", disabled: brandDisabled, disabledTooltip: BRAND_INACTIVE_TIP },
                          { id: "affiliate", label: "Creators" },
                          {
                            id: "brand",
                            label: "Merchant",
                            disabled: brandDisabled,
                            disabledTooltip: BRAND_INACTIVE_TIP,
                          },
                        ]}
                        activeTab={field.value ?? (brandDisabled ? "affiliate" : "all")}
                        onTabChange={(tabId) => field.onChange(tabId === "all" ? undefined : tabId)}
                      />
                    </div>
                  )}
                />
              )}
              {isActive("creatorLevel") && (advancedFilters?.creatorLevel?.length ?? 0) > 0 && (
                <FormField
                  control={form.control}
                  name="creatorLevel"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-2">
                      <FormLabel className="text-sm text-tertiary">Creator Level</FormLabel>
                      <PillField
                        value={field.value}
                        options={advancedFilters?.creatorLevel ?? []}
                        onChange={field.onChange}
                        getUnavailableReason={unavailable.creatorLevel}
                      />
                    </FormItem>
                  )}
                />
              )}
              {isActive("minFollowers") && isActive("maxFollowers") && (
                <UnavailableRange
                  form={form}
                  fields={["minFollowers", "maxFollowers"]}
                  reason={zeroMaxReason(availabilityRanges?.maxFollowers, VIDEO_UNAVAILABLE_REASONS.followers)}
                >
                  <FollowersRangeFilter form={form} max={videoFilterRanges?.maxFollowers || undefined} />
                </UnavailableRange>
              )}
              {isActive("maleAudiencePercent") && (
                <FormField
                  control={form.control}
                  name="maleAudiencePercent"
                  render={({ field }) => (
                    <div className="flex flex-col gap-2">
                      <Label className="text-sm text-tertiary">Male Audience</Label>
                      <PercentileSelector
                        value={(field.value as PercentileValue) ?? "all"}
                        onChange={(val) => field.onChange(val === "all" ? undefined : val)}
                        getUnavailableReason={unavailable.maleAudiencePercent}
                      />
                    </div>
                  )}
                />
              )}
              {isActive("femaleAudiencePercent") && (
                <FormField
                  control={form.control}
                  name="femaleAudiencePercent"
                  render={({ field }) => (
                    <div className="flex flex-col gap-2">
                      <Label className="text-sm text-tertiary">Female Audience</Label>
                      <PercentileSelector
                        value={(field.value as PercentileValue) ?? "all"}
                        onChange={(val) => field.onChange(val === "all" ? undefined : val)}
                        getUnavailableReason={unavailable.femaleAudiencePercent}
                      />
                    </div>
                  )}
                />
              )}
              {isActive("age") && (
                <FormField
                  control={form.control}
                  name="age"
                  render={({ field }) => (
                    <div className="flex flex-col gap-2">
                      <LabelWithTooltip
                        label="Audience Age Range"
                        tooltip="Audience Age Range"
                        description="Age group making up >30% of audience."
                      />
                      <PillField
                        value={field.value}
                        options={["18-24", "25-34", "35-44", "45-54", "55+"]}
                        onChange={field.onChange}
                        getUnavailableReason={unavailable.age}
                      />
                    </div>
                  )}
                />
              )}
            </FilterSection>
          )}

          {/* Featured Products Section */}
          {isActive("product") && (
            <FilterSection
              id="featured-products"
              icon={<ShoppingBag01 className="w-5 h-5" />}
              title="Featured Products"
            >
              <FormField
                control={form.control}
                name="product"
                render={({ field }) => <ProductFilter field={field} />}
              />
            </FilterSection>
          )}

          {/* Collaborations Section */}
          {(isActive("collaborationType") || isActive("collaboration")) && (
            <FilterSection id="collaborations" icon={<Announcement01 className="w-5 h-5" />} title="Collaborations">
              {isActive("collaborationType") && (
                <FormField
                  control={form.control}
                  name="collaborationType"
                  render={({ field }) => (
                    <div className="flex flex-col gap-2">
                      <Label className="text-sm text-tertiary">Collaboration Type</Label>
                      <HorizontalTabs
                        tabs={[
                          { id: "all", label: "All" },
                          {
                            id: "target",
                            label: "Target",
                            ...tabUnavailability(
                              unavailableUnless(
                                availability?.collaborationType,
                                VIDEO_UNAVAILABLE_REASONS.targetCollaboration,
                              )?.("target"),
                              field.value === "target",
                            ),
                          },
                          {
                            id: "open",
                            label: "Open",
                            ...tabUnavailability(
                              unavailableUnless(
                                availability?.collaborationType,
                                VIDEO_UNAVAILABLE_REASONS.openCollaboration,
                              )?.("open"),
                              field.value === "open",
                            ),
                          },
                        ]}
                        activeTab={field.value ?? "all"}
                        onTabChange={(tabId) => field.onChange(tabId === "all" ? undefined : tabId)}
                      />
                    </div>
                  )}
                />
              )}
              {isActive("collaboration") && (
                <FormField
                  control={form.control}
                  name="collaboration"
                  render={({ field }) => <CollaborationFilter field={field} />}
                />
              )}
            </FilterSection>
          )}

          {isActive("campaign") && (
            <FilterSection id="ad-campaigns" icon={<Announcement03 className="w-5 h-5" />} title="Ad Campaigns">
              <FormField
                control={form.control}
                name="campaign"
                render={({ field }) => <CampaignFilter field={field} />}
              />
            </FilterSection>
          )}
        </Accordion>
      </div>
    </Form>
  );
};
